export const name = "CHANGES";
export const summary = `Returns all rows that have changed in a table for a given time range.`;
export const reference = `
## CHANGES

Returns all rows that have changed in a table for a given time range.



### 

\`\`\`
CHANGES(
  TABLE table,
  start_timestamp DEFAULT NULL,
  end_timestamp)
\`\`\`


### Description

The \`CHANGES\` function returns all rows that have changed in a table for a given time range. To use the \`CHANGES\` function on a table, you must set the table's \`enable_change_history\` optionto \`TRUE\`.

The following operations add rows to the \`CHANGES\` change history:

- \`CREATE TABLE\` DDL statement
- \`INSERT\` DML statement
- Data appended, changed or deleted as part of a \`MERGE\` DML statement
- \`UPDATE\` DML statement
- \`DELETE\` DML statement
- Loading data into BigQuery
- Streaming ingestion
- \`TRUNCATE TABLE\` DML statement
- Jobs configured with a \`writeDisposition\` of \`WRITE_TRUNCATE\`
- Individual table partition deletions


### Definitions

- \`table\`: the BigQuery table name. This must be a regular BigQuery table, and must have the \`enable_change_history\`option set to \`TRUE\`. Enabling this table option has an impact on costs; for more information see Pricing and costs. This argument must be preceded by the word \`TABLE\`.
- \`start_timestamp\`: a \`TIMESTAMP\` value indicating the earliest time at which a change is included in the output. If the value is \`NULL\`, all changes since the table creation are returned. If you set the\`enable_change_history\` option after setting the \`start_timestamp\` option, the history before the enablement time might be incomplete. If the table was created after the \`start_timestamp\` value, the actual table creation time is used instead. An error is returned if the time specified is earlier than allowed by time travel, or if the table was created earlier than allowed by time travel if the \`start_timestamp\` value is \`NULL\`. For standard tables, this window is seven days, but you can configure the time travel window to be less than that.
- \`end_timestamp\`: a \`TIMESTAMP\` value indicating the latest time at which a change is included in the output. \`end_timestamp\` is exclusive; for example, if you specify \`2023-12-31 08:00:00\` for \`start_timestamp\` and \`2023-12-31
12:00:00\` for \`end_timestamp\`, all changes made from 8 AM December 31, 2023 through 11:59 AM December 31, 2023 are returned. The maximum time range allowed between \`start_timestamp\` and \`end_timestamp\` is one day. For a batch query, \`end_timestamp\` must be at least ten minutes prior to the current time. For a continuous query, set this as \`NULL\`to return the latest data.


### Details

If a row is inserted, a record of the new row with an \`INSERT\` change type is produced.

If a row is deleted, a record of the deleted row with a \`DELETE\` change type is produced.

If a row is updated, a record of the old row with a \`DELETE\` change type and a record of the new row with an \`UPDATE\` change type are produced.


### Output

The \`CHANGES\` function returns a table with the following columns:

- All columns of the input table at the time that the query is run. If a column is added after the \`end_timestamp\` value, it appears with \`NULL\` values populated in of the any rows that were changed before the addition of the column.
- \`_CHANGE_TYPE\`: a \`STRING\` value indicating the type of change that produced the row. For \`CHANGES\`, the supported values are \`INSERT\`, \`UPDATE\`, and\`DELETE\`.
- \`_CHANGE_TIMESTAMP\`: a \`TIMESTAMP\` value indicating the commit time of the transaction that made the change.


### Limitations

- The data returned by the \`CHANGES\` function is limited to the time travel window of the table.
- The data returned by the \`CHANGES\` function is limited to the table's current schema.
- The maximum allowed time range between the \`start_timestamp\` and\`end_timestamp\` arguments you specify for the function is one day.
- The \`CHANGES\` function can't query the last ten minutes of table history. Therefore, the \`end_timestamp\` argument value must be at least ten minutes prior to the current time.
- You can't call the \`CHANGES\` function within a multi-statement transaction.
- You can't use the \`CHANGES\` function with tables that have had multi-statement transactions committed to them within the requested time window.
- You can only use the \`CHANGES\` function with regular BigQuery tables. Views, materialized views, external tables, and wildcard tables aren't supported.
- For tables that have been cloned or snapshotted, and for tables that are restored from a clone or snapshot, change history from the source table isn't carried over to the new table, clone, or snapshot.
- You can't use the \`CHANGES\` function with a table that has change data capture enabled.
- Partition pseudo-columns for ingestion-time partitioned tables, such as\`_PARTITIONTIME\` and \`_PARTITIONDATE\`, aren't included in the function's output.
- Change history isn't captured for table deletions made due to table partition expiration.
- Performing data manipulation language (DML) statements over recently streamed datafails on tables that have the \`enable_change_history\` option set to \`TRUE\`.


### Example

This example shows the change history returned by the \`CHANGES\` function as various changes are made to a table called \`Produce\`. First, create the table:



\`\`\`
CREATE TABLE mydataset.Produce (
  product STRING,
  inventory INT64)
OPTIONS(enable_change_history=true);
\`\`\`

Insert two rows into the table:



\`\`\`
INSERT INTO mydataset.Produce
VALUES
  ('bananas', 20),
  ('carrots', 30);
\`\`\`

Delete one row from the table:



\`\`\`
DELETE mydataset.Produce
WHERE product = 'bananas';
\`\`\`

Update one row of the table:



\`\`\`
UPDATE mydataset.Produce
SET inventory = inventory - 10
WHERE product = 'carrots';
\`\`\`

Wait for 10 minutes and view the full change history of the changes:



\`\`\`
SELECT
  product,
  inventory,
  _CHANGE_TYPE AS change_type,
  _CHANGE_TIMESTAMP AS change_time
FROM
  CHANGES(TABLE mydataset.Produce, NULL, TIMESTAMP_SUB(CURRENT_TIMESTAMP(), INTERVAL 601 SECOND))
ORDER BY change_time, product;
\`\`\`

The output is similar to the following:



\`\`\`
+---------+-----------+-------------+---------------------+
| product | inventory | change_type | change_time |
+---------+-----------+-------------+---------------------+
| bananas | 20 | INSERT | 2024-01-09 17:13:58 |
| carrots | 30 | INSERT | 2024-01-09 17:13:58 |
| bananas | 20 | DELETE | 2024-01-09 17:14:30 |
| carrots | 30 | DELETE | 2024-01-09 17:15:24 |
| carrots | 20 | UPDATE | 2024-01-09 17:15:24 |
+---------+-----------+-------------+---------------------+
\`\`\`


### Enabling change history for an existing table

To set the \`enable_change_history\` optionto \`TRUE\` for an existing table, use the \`ALTER TABLE SET OPTIONS\` DDL statement. The following example updates the change history option for \`my_table\` to\`TRUE\`:



\`\`\`
ALTER TABLE \`my_dataset.my_table\`
SET OPTIONS (enable_change_history = TRUE);
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/time-series-functions
`;
